import React from 'react';
import ReactDOM from 'react-dom/client';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PrivateRoute from './layouts/PrivateRoute';
import PublicRoute from './layouts/PublicRoute';

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Box } from '@mui/material';

import DashboardHeader from './layouts/DashboardHeader';
import DashboardLayout from './layouts/DashboardLayout';
import PublicLayout from './layouts/PublicLayout';

import ErrorPage from './pages/ErrorPage';

import Home from './pages/Home';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import Privacy from './pages/PrivacyPolicy';
import Terms from './pages/TermsOfUse';
import InstapathPilot from './pages/InstapathPilot';
import AuthMgr from './pages/AuthMgr';
import SignIn from './pages/SignIn';
import VerifyCode from './pages/VerifyCode';
import SignUp from './pages/SignUp';
import Subscribe from './pages/Subscribe';
import MobilePlatform from './pages/platform/MobilePlatform';
import PilotsEdPlatform from './pages/platform/PilotsEdPlatform';
import InstapathPlatform from './pages/platform/InstapathPlatform';
import Blog from './pages/blog/Blog';
import BlogPost from './pages/blog/BlogPost';

import Account from './pages/Account';
import Billing from './pages/Billing';

import Dashboard from './pages/Dashboard';
import PlanMission from './pages/PlanMission';
import Documents from './pages/Documents';
import Flights from './pages/Flights';
import Calendar from './pages/Calendar';
import SolvPro from './pages/SolvPro';

import Customers from './pages/Customers';
import CustomerDetails from './pages/CustomerDetails';
import Missions from './pages/Missions';
import MissionPlanner from './pages/MissionPlanner';
import Assets from './pages/Assets';
import FlightLogs from './pages/FlightLogs';
import Maintenance from './pages/Maintenance';
import Incidents from './pages/Incidents';
import Waypoints from './pages/Waypoints';
import Personnel from './pages/Personnel';
import PersonnelDetails from './pages/PersonnelDetails';
import Reports from './pages/Reports';
import Admin from './pages/Admin';
import PredefinedShotsEditor from './pages/settings/PredefinedShotsEditor';
import ManageSubscription from './pages/ManageSubscription';
import SubscriptionFAQ from './pages/SubscriptionFAQ';

import MissionSummary from './pages/reports/MissionSummary';
import MissionCompletion from './pages/reports/MissionCompletion';
import PilotPerformance from './pages/reports/PilotPerformance';

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <Box sx={{ display: 'flex', height: '100vh' }}>

          {/* Main Content */}
          <Box sx={{ flexGrow: 1 }}>

            {/* Page Routes */}
            <Routes>
              {/* Public Routes */}
              <Route path="/error" element={<PublicLayout><ErrorPage /></PublicLayout>} />

              <Route path="/" element={<PublicRoute><PublicLayout><Home /></PublicLayout></PublicRoute>} />
              <Route path="/pricing" element={<PublicRoute><PublicLayout><Pricing /></PublicLayout></PublicRoute>} />
              <Route path="/blog" element={<PublicRoute><PublicLayout><Blog /></PublicLayout></PublicRoute>} />
              <Route path="/blog/:slug" element={<PublicRoute><PublicLayout><BlogPost /></PublicLayout></PublicRoute>} />
              <Route path="/contact" element={<PublicRoute><PublicLayout><Contact /></PublicLayout></PublicRoute>} />
              <Route path="/privacy-policy" element={<PublicRoute><PublicLayout><Privacy /></PublicLayout></PublicRoute>} />
              <Route path="/terms-of-use" element={<PublicRoute><PublicLayout><Terms /></PublicLayout></PublicRoute>} />
              <Route path="/instapath-pilot" element={<PublicRoute><PublicLayout><InstapathPilot /></PublicLayout></PublicRoute>} />
              <Route path="/authmgr" element={<PublicRoute><PublicLayout><AuthMgr /></PublicLayout></PublicRoute>} />
              <Route path="/signin" element={<PublicRoute><PublicLayout><SignIn /></PublicLayout></PublicRoute>} />
              <Route path="/verify-code" element={<PublicRoute><PublicLayout><VerifyCode /></PublicLayout></PublicRoute>} />
              <Route path="/signup" element={<PublicRoute><PublicLayout><SignUp /></PublicLayout></PublicRoute>} />
              <Route path="/subscribe" element={<PublicRoute><PublicLayout><Subscribe /></PublicLayout></PublicRoute>} />
              <Route path="/platform/instapath" element={<PublicRoute><PublicLayout><InstapathPlatform /></PublicLayout></PublicRoute>} />
              <Route path="/platform/mobile" element={<PublicRoute><PublicLayout isFullWidth><MobilePlatform /></PublicLayout></PublicRoute>} />
              <Route path="/platform/pilots-ed" element={<PublicRoute><PublicLayout isFullWidth><PilotsEdPlatform /></PublicLayout></PublicRoute>} />

              {/* Account Routes */}
              <Route path="/account" element={<PrivateRoute><DashboardLayout><Account /></DashboardLayout></PrivateRoute>} />
              <Route path="/billing" element={<PrivateRoute><DashboardLayout><Billing /></DashboardLayout></PrivateRoute>} />

              {/* Dashboard Routes */}
              <Route path="/dashboard" element={<PrivateRoute><DashboardLayout><Dashboard /></DashboardLayout></PrivateRoute>} />
              <Route path="/plan-mission" element={<PrivateRoute><DashboardLayout><PlanMission /></DashboardLayout></PrivateRoute>} />
              <Route path="/documents" element={<PrivateRoute><DashboardLayout><Documents /></DashboardLayout></PrivateRoute>} />
              <Route path="/flights" element={<PrivateRoute><DashboardLayout><Flights /></DashboardLayout></PrivateRoute>} />
              <Route path="/calendar" element={<PrivateRoute><DashboardLayout><Calendar /></DashboardLayout></PrivateRoute>} />
              <Route path="/solvpro" element={<PrivateRoute><DashboardLayout><SolvPro /></DashboardLayout></PrivateRoute>} />
              <Route path="/customers" element={<PrivateRoute><DashboardLayout><Customers /></DashboardLayout></PrivateRoute>} />
              <Route path="/customers/new" element={<PrivateRoute><DashboardLayout><CustomerDetails /></DashboardLayout></PrivateRoute>} />
              <Route path="/customers/:customerGuid" element={<PrivateRoute><DashboardLayout><CustomerDetails mode="edit" /></DashboardLayout></PrivateRoute>} />
              <Route path="/missions" element={<PrivateRoute><DashboardLayout><Missions /></DashboardLayout></PrivateRoute>} />
              <Route path="/mission/:missionGuid" element={<PrivateRoute><DashboardLayout><MissionPlanner /></DashboardLayout></PrivateRoute>} />
              <Route path="/assets" element={<PrivateRoute><DashboardLayout><Assets /></DashboardLayout></PrivateRoute>} />
              <Route path="/flight-logs" element={<PrivateRoute><DashboardLayout><FlightLogs /></DashboardLayout></PrivateRoute>} />
              <Route path="/maintenance" element={<PrivateRoute><DashboardLayout><Maintenance /></DashboardLayout></PrivateRoute>} />
              <Route path="/incidents" element={<PrivateRoute><DashboardLayout><Incidents /></DashboardLayout></PrivateRoute>} />
              <Route path="/waypoints" element={<PrivateRoute><DashboardLayout><Waypoints /></DashboardLayout></PrivateRoute>} />
              <Route path="/personnel" element={<PrivateRoute><DashboardLayout><Personnel /></DashboardLayout></PrivateRoute>} />
              <Route path="/personnel/new" element={<PrivateRoute><DashboardLayout><PersonnelDetails /></DashboardLayout></PrivateRoute>} />
              <Route path="/personnel/:userGuid" element={<PrivateRoute><DashboardLayout><PersonnelDetails mode="edit" /></DashboardLayout></PrivateRoute>} />
              <Route path="/reports" element={<PrivateRoute><DashboardLayout><Reports /></DashboardLayout></PrivateRoute>} />
              <Route path="/admin" element={<PrivateRoute><DashboardLayout><Admin /></DashboardLayout></PrivateRoute>} />
              <Route path="/settings/predefined-shots" element={<PrivateRoute><DashboardLayout><PredefinedShotsEditor /></DashboardLayout></PrivateRoute>} />
              <Route path="/manage-subscription" element={<PrivateRoute><DashboardLayout><ManageSubscription /></DashboardLayout></PrivateRoute>} />
              <Route path="/faq/subscription" element={<PrivateRoute><DashboardLayout><SubscriptionFAQ /></DashboardLayout></PrivateRoute>} />

              <Route path="/reports/mission-summary" element={<PrivateRoute><DashboardLayout><MissionSummary /></DashboardLayout></PrivateRoute>} />
              <Route path="/reports/mission-completion" element={<PrivateRoute><DashboardLayout><MissionCompletion /></DashboardLayout></PrivateRoute>} />
              <Route path="/reports/pilot-performance" element={<PrivateRoute><DashboardLayout><PilotPerformance /></DashboardLayout></PrivateRoute>} />

              {/* Catch-All Route */}
              <Route path="*" element={<PublicLayout><ErrorPage /></PublicLayout>} />

            </Routes>
          </Box>
        </Box>
      </Router>
    </LocalizationProvider>
  );
}

export default App;
